import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

interface Address {
  id: string;
  text: string;
  coordinates: [number, number];
}

interface RouteMapProps {
  addresses: Address[];
}

const RouteMap: React.FC<RouteMapProps> = ({ addresses }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const distanceMarkers = useRef<mapboxgl.Marker[]>([]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;

    // Set access token if not already set
    const token = process.env.REACT_APP_MAPBOX_TOKEN || '';
    if (!mapboxgl.accessToken) {
      mapboxgl.accessToken = token;
    }

    // If map already exists, remove it
    if (map.current) {
      map.current.remove();
      map.current = null;
    }

    // Create new map instance
    const newMap = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: addresses.length > 0 ? addresses[0].coordinates : [-71.0589, 42.3601], // Default to Boston
      zoom: addresses.length > 0 ? 12 : 10,
      pitch: 0,
    });

    // Add navigation control
    newMap.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Store the map instance
    map.current = newMap;

    // Clean up function
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [addresses]); // Re-initialize when addresses change

  // Update markers and route
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap || !addresses.length) return;

    // Wait for map to load before adding sources and layers
    const updateMapFeatures = () => {
      // Clean up existing markers
      markers.current.forEach(marker => marker.remove());
      markers.current = [];
      distanceMarkers.current.forEach(marker => marker.remove());
      distanceMarkers.current = [];

      // Clean up existing route
      if (currentMap.getSource('route')) {
        try {
          currentMap.removeLayer('route');
          currentMap.removeSource('route');
        } catch (error) {
          console.log('Error removing existing route:', error);
        }
      }

      // Add markers for each address
      addresses.forEach((address, index) => {
        const markerElement = document.createElement('div');
        markerElement.className = 'marker-wrapper';

        const marker = document.createElement('div');
        marker.className = `marker ${address.id === 'user-location' ? 'user-location' : ''}`;
        marker.textContent = address.id === 'user-location' ? '📍' : `${index}`;

        const label = document.createElement('div');
        label.className = 'address-label';
        label.textContent = address.text;

        markerElement.appendChild(marker);
        markerElement.appendChild(label);

        const mapMarker = new mapboxgl.Marker({
          element: markerElement,
          anchor: 'bottom',
          offset: [0, -1]
        })
          .setLngLat(address.coordinates)
          .addTo(currentMap);

        markers.current.push(mapMarker);
      });

      // Fit bounds to include all markers
      if (addresses.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        addresses.forEach(address => {
          bounds.extend(address.coordinates);
        });

        currentMap.fitBounds(bounds, {
          padding: { top: 50, bottom: 50, left: 50, right: 50 },
          maxZoom: 15
        });
      }

      // Draw route line if we have at least 2 addresses
      if (addresses.length >= 2) {
        try {
          currentMap.addSource('route', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: addresses.map(addr => addr.coordinates)
              }
            }
          });

          currentMap.addLayer({
            id: 'route',
            type: 'line',
            source: 'route',
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': '#3b82f6',
              'line-width': 4,
              'line-opacity': 0.8
            }
          });

          // Add distance markers
          for (let i = 0; i < addresses.length - 1; i++) {
            const start = addresses[i].coordinates;
            const end = addresses[i + 1].coordinates;
            const center = [
              (start[0] + end[0]) / 2,
              (start[1] + end[1]) / 2
            ];

            const distance = calculateDistance(start, end);
            
            const distanceElement = document.createElement('div');
            distanceElement.className = 'distance-container';
            distanceElement.textContent = `${distance.toFixed(1)} mi`;

            const distanceMarker = new mapboxgl.Marker({
              element: distanceElement,
              anchor: 'center'
            })
              .setLngLat(center as [number, number])
              .addTo(currentMap);

            distanceMarkers.current.push(distanceMarker);
          }
        } catch (error) {
          console.error('Error adding route:', error);
        }
      }
    };

    // If the map is already loaded, update features immediately
    if (currentMap.loaded()) {
      updateMapFeatures();
    } else {
      // Otherwise wait for the load event
      currentMap.once('load', updateMapFeatures);
    }

    // Cleanup function
    return () => {
      if (currentMap && currentMap.loaded()) {
        markers.current.forEach(marker => marker.remove());
        markers.current = [];
        distanceMarkers.current.forEach(marker => marker.remove());
        distanceMarkers.current = [];
        
        if (currentMap.getSource('route')) {
          try {
            currentMap.removeLayer('route');
            currentMap.removeSource('route');
          } catch (error) {
            console.log('Error cleaning up route:', error);
          }
        }
      }
    };
  }, [addresses]);

  const calculateDistance = (coord1: [number, number], coord2: [number, number]): number => {
    const R = 3959; // Earth's radius in miles
    const lat1 = coord1[1] * Math.PI / 180;
    const lat2 = coord2[1] * Math.PI / 180;
    const deltaLat = (coord2[1] - coord1[1]) * Math.PI / 180;
    const deltaLon = (coord2[0] - coord1[0]) * Math.PI / 180;

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  return (
    <div className="relative w-full h-[500px]">
      <div ref={mapContainer} className="map-container absolute inset-0" />
    </div>
  );
};

export default RouteMap; 